import { ArrowDown, Loader2 } from 'lucide-react'
import { usePullToRefresh, type UsePullToRefreshOptions } from '@/hooks/usePullToRefresh'

interface PullToRefreshProps extends UsePullToRefreshOptions {
    children: React.ReactNode
}

/**
 * Wraps a scrollable area with pull-to-refresh gesture support.
 * Renders a visual indicator above the content that responds to the pull distance.
 *
 * On touch devices, pulling down from the top of the page reveals an arrow
 * that flips to indicate "release to refresh", then transitions to a spinner.
 */
export function PullToRefresh({
    children,
    onRefresh,
    threshold = 80,
    maxPull = 150,
    disabled = false,
}: PullToRefreshProps) {
    const { pullDistance, isRefreshing, containerRef } = usePullToRefresh({
        onRefresh,
        threshold,
        maxPull,
        disabled,
    })

    const isActive = pullDistance > 0 || isRefreshing
    const pastThreshold = pullDistance >= threshold

    // Progress from 0 → 1 as pull approaches the threshold
    const progress = Math.min(pullDistance / threshold, 1)

    return (
        <div ref={containerRef}>
            {/* Pull indicator — only rendered when actively pulling */}
            {isActive && (
                <div
                    className="flex items-center justify-center overflow-hidden"
                    style={{ height: pullDistance }}
                >
                    <div
                        className="flex items-center justify-center w-9 h-9 rounded-full bg-surface-light dark:bg-surface-dark border border-border-light dark:border-border-dark shadow-sm"
                        style={{
                            opacity: Math.min(progress * 1.5, 1),
                            transform: `scale(${0.5 + progress * 0.5})`,
                        }}
                    >
                        {isRefreshing ? (
                            <Loader2 className="w-5 h-5 text-primary animate-spin" />
                        ) : (
                            <ArrowDown
                                className="w-5 h-5 text-text-secondary-light dark:text-text-secondary-dark transition-transform duration-200"
                                style={{
                                    transform: pastThreshold ? 'rotate(180deg)' : 'rotate(0deg)',
                                }}
                            />
                        )}
                    </div>
                </div>
            )}
            {children}
        </div>
    )
}
